/**
 * ProgrammeReportPicker.tsx — index for automated per-programme outcome reports (VER-109).
 *
 * Route: /assess/reports
 *
 * Lists the organisation's programmes (org-scoped by the API) and links each to its
 * outcome report at /assess/report/:programmeId. Reports are most meaningful once a
 * programme has completed, but every programme is shown — the report page itself shows
 * an appropriate empty state when there is not yet enough paired data.
 *
 * TODO [RBAC]: guarded with permission="DashboardView" in Routes.tsx — swap to the
 * SurveyReportView permit once it exists on the frontend. Run verbal-rbac-review before merge.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Divider, List, ListItemButton, ListItemText, Stack, Typography } from "@mui/material";
import { ChevronRightOutlined, InsightsOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Spinner from "../../layout/Spinner";
import { SimpleBlankSlate } from "../../../core/components/SimpleBlankSlate";
import { VPill, VPillPositive } from "../../../core/components/VPill";
import { list as listProgrammes } from "../../../client/api/programme";
import { ProgrammeType } from "../../../models/modelTypes";

const formatStartDate = (value: Date | string | null | undefined): string => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? ""
        : date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const StatePill = ({ state }: { state: ProgrammeType["state"] }): JSX.Element => {
    if (state === "Complete") return <VPillPositive size="small" label="Complete" />;
    if (state === "InProgress") return <VPill size="small" label="In progress" color="primary" />;
    return <VPill size="small" label="Not started" />;
};

export const ProgrammeReportPicker = (): JSX.Element => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [programmes, setProgrammes] = useState<ProgrammeType[]>([]);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);

        listProgrammes({})
            .then((response) => {
                if (active) setProgrammes(response.items);
            })
            .catch(() => {
                if (active) setError("We couldn't load your programmes. Please try again.");
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, []);

    return (
        <Box p={4} width={1}>
            <Box mb={1}>
                <Typography variant="overline" color="textSecondary">
                    Assess
                </Typography>
                <Typography variant="h3" component="h1">
                    Programme outcome reports
                </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Choose a programme to see its automated wellbeing outcome report — descriptive statistics, a
                baseline-vs-endpoint comparison, and a plain-language summary. Results are group-level; individual
                scores are available as a raw CSV export from each report.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {loading ? (
                <Spinner />
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : programmes.length === 0 ? (
                <SimpleBlankSlate
                    header="No programmes yet"
                    content="Once you have created a programme and participants have completed their outcome measures, its report will appear here."
                />
            ) : (
                <List disablePadding>
                    {programmes.map((programme) => {
                        const startDate = formatStartDate(programme.startDate);
                        return (
                            <ListItemButton
                                key={programme.id}
                                onClick={() => navigate(`/assess/report/${programme.id}`)}
                                sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    mb: 1,
                                }}
                            >
                                <InsightsOutlined color="primary" sx={{ mr: 2 }} />
                                <ListItemText
                                    primary={programme.name}
                                    secondary={startDate ? `Started ${startDate}` : undefined}
                                />
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <StatePill state={programme.state} />
                                    <ChevronRightOutlined color="action" />
                                </Stack>
                            </ListItemButton>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};
